import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EllipsisVerticalIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  KeyIcon,
  UsersIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { CreateUserModal } from './CreateUserModal';
import { EditUserModal } from './EditUserModal';
import { UserGroupsModal } from './UserGroupsModal';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { useSearch } from '../../../contexts/SearchContext';
import apiClient from '../../../api/axios';
import type { TaskReminderProfile } from '../../../types/taskReminders';
import type { SignatureUserProfile } from '../types/digitalSignatureOnboarding';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isAttorney: boolean;
  isActive: boolean;
  roles: string[];
  createdAt: string;
  updatedAt: string;
  smsNumber?: string | null;
  reminderProfile?: TaskReminderProfile | null;
  signatureProfile?: SignatureUserProfile | null;
}

interface UsersResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface UserFilters {
  role: string;
  isActive: string;
  search: string;
}

export const UsersTab: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userFilters, setUserFilters] = useState<UserFilters>({
    role: '',
    isActive: '',
    search: ''
  });
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [userToDeactivate, setUserToDeactivate] = useState<User | null>(null);
  const [deactivationReason, setDeactivationReason] = useState<string>('termination');
  const [userToReactivate, setUserToReactivate] = useState<User | null>(null);
  const [userToResetPassword, setUserToResetPassword] = useState<User | null>(null);
  const [userForGroupManagement, setUserForGroupManagement] = useState<User | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<keyof User>('firstName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Toast notification system
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type: 'success' | 'error' | 'warning'; timestamp: number }>>([]);
  
  const showToast = (message: string, type: 'success' | 'error' | 'warning' = 'success') => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const newToast = { id, message, type, timestamp: Date.now() };
    setToasts(prev => [...prev, newToast]);
    
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 5000);
  };
  
  const dismissToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };
  
  const userPermissions = useUserPermissions();
  const canManageUsers = userPermissions.roles.includes('admin');
  const queryClient = useQueryClient();
  const { debouncedSearchQuery } = useSearch();

  // Use global search query if available, otherwise use local search term
  const effectiveSearchTerm = debouncedSearchQuery || searchTerm;

  // Fetch users with React Query using Axios
  const { data, isLoading, error, refetch } = useQuery<UsersResponse>({
    queryKey: ['users', currentPage, effectiveSearchTerm, userFilters.role, userFilters.isActive],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '25',
        ...(effectiveSearchTerm && { search: effectiveSearchTerm }),
        ...(userFilters.role && { role: userFilters.role }),
        ...(userFilters.isActive && { isActive: userFilters.isActive })
      });

      const response = await apiClient.get(`/admin/users?${params}`);
      return response.data;
    },
    staleTime: 30000,
  });

  // Mutations
  const deactivateUserMutation = useMutation({
    mutationFn: async ({ userId, reason }: { userId: string; reason: string }) => {
      await apiClient.patch(`/admin/users/${userId}/deactivate`, { reason });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setUserToDeactivate(null);
      setDeactivationReason('termination');
      showToast('User deactivated successfully', 'success');
    },
    onError: () => {
      showToast('Failed to deactivate user', 'error');
    }
  });

  const reactivateUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.patch(`/admin/users/${userId}/reactivate`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setUserToReactivate(null);
    }
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.delete(`/admin/users/${userId}/permanent`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setUserToDelete(null);
      showToast('User permanently deleted', 'success');
    },
    onError: (error: any) => {
      setUserToDelete(null);
      const errorMessage = error.response?.data?.description || error.response?.data?.message || error.message || 'Failed to delete user.';
      showToast(errorMessage, 'error');
    }
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await apiClient.post(`/admin/users/${userId}/reset-password`);
      return response.data;
    },
    onSuccess: (data) => {
      setUserToResetPassword(null);
      
      if (data.success) {
        if (data.emailSent) {
          showToast(data.message || 'Password reset email sent successfully!', 'success');
        } else {
          showToast(data.message || 'Password reset initiated, but email notification failed. User may need to contact support.', 'warning');
        }
      } else {
        showToast(data.message || 'Password reset failed. Please try again.', 'error');
      }
    },
    onError: (error: any) => {
      setUserToResetPassword(null);
      const errorMessage = error.response?.data?.message || error.message || 'Password reset failed. Please try again.';
      showToast(errorMessage, 'error');
    }
  });

  const resendInvitationMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await apiClient.post(`/admin/users/${userId}/resend-invitation`);
      return response.data;
    },
    onSuccess: (data) => {
      showToast(data.message || 'Invitation email resent successfully!', 'success');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to resend invitation.';
      showToast(errorMessage, 'error');
    }
  });

  // Filtered and sorted users
  const filteredUsers = React.useMemo(() => {
    if (!data?.users) return [];
    
    const filtered = data.users.filter((user) => {
      const matchesSearch = !effectiveSearchTerm || 
        user.firstName.toLowerCase().includes(effectiveSearchTerm.toLowerCase()) ||
        user.lastName.toLowerCase().includes(effectiveSearchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(effectiveSearchTerm.toLowerCase());
      
      const matchesRole = !userFilters.role || user.roles.includes(userFilters.role);
      const matchesStatus = !userFilters.isActive || user.isActive.toString() === userFilters.isActive;
      
      return matchesSearch && matchesRole && matchesStatus;
    });

    // Sort users
    filtered.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortDirection === 'asc' ? 1 : -1;
      if (bValue == null) return sortDirection === 'asc' ? -1 : 1;
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [data?.users, effectiveSearchTerm, userFilters, sortField, sortDirection]);

  const handleSort = (field: keyof User) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSelectAll = () => {
    if (selectedUsers.size === filteredUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(filteredUsers.map(u => u.id)));
    }
  };

  const handleSelectUser = (userId: string) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
        Active
      </span>
    ) : (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
        Inactive
      </span>
    );
  };

  const isSignatureReady = (user: User): boolean => {
    const profile = user.signatureProfile;
    if (!profile) return false;
    const hasDisplayName = Boolean(profile.signer_display_name && profile.signer_display_name.trim().length > 0);
    const canSign = profile.can_sign_documents === true;
    const mfaReady = profile.mfa_confirmed_for_signing === true;
    const hasConsent = Boolean(profile.esign_consent_accepted_at && profile.esign_consent_accepted_at.trim().length > 0);
    return hasDisplayName && canSign && mfaReady && hasConsent;
  };

  // Utility function to highlight search terms
  const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 dark:bg-yellow-800/50 px-1 rounded">
          {part}
        </mark>
      ) : part
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading users...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 dark:text-red-400 mb-4">
          Failed to load users. Please try again.
        </div>
        <Button onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls Bar */}
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="px-4 sm:px-6 py-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 lg:gap-4">
              {/* Left side - Search and Filters */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-1 min-w-0">
                {/* Search */}
                <div className="relative flex-1 max-w-md">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search users by name or email..."
                    value={effectiveSearchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                {/* Filters */}
                <div className="flex gap-2 flex-shrink-0">
                  <select
                    value={userFilters.role}
                    onChange={(e) => setUserFilters(prev => ({ ...prev, role: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">All Roles</option>
                    <option value="admin">Admin</option>
                    <option value="attorney">Attorney</option>
                    <option value="billing">Billing</option>
                    <option value="assistant">Assistant</option>
                    <option value="user">User</option>
                  </select>
                  <select
                    value={userFilters.isActive}
                    onChange={(e) => setUserFilters(prev => ({ ...prev, isActive: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">All Status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Right side - Count and Add button */}
              <div className="flex items-center justify-between sm:justify-start gap-3 lg:gap-4 flex-shrink-0">
                <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                  {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
                </div>
                
                {canManageUsers && (
                  <Button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2">
                    <PlusIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">Add User</span>
                    <span className="sm:hidden">Add</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Bulk actions bar */}
        {selectedUsers.size > 0 && (
          <div className="bg-lex-accent-soft border-b border-lex-accent/30 px-6 py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-lex-accent">
                {selectedUsers.size} user{selectedUsers.size !== 1 ? 's' : ''} selected
              </span>
              <div className="flex space-x-2">
                <button className="px-3 py-1 text-sm bg-lex-accent text-lex-accent-contrast rounded hover:bg-lex-accent-hover">
                  Export
                </button>
                <button className="px-3 py-1 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700">
                  Bulk Deactivate
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left w-12">
                  <input
                    type="checkbox"
                    checked={selectedUsers.size === filteredUsers.length && filteredUsers.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                  />
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort('firstName')}
                >
                  Name
                  {sortField === 'firstName' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort('email')}
                >
                  Email
                  {sortField === 'email' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Roles
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Digital Signature
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort('isActive')}
                >
                  Status
                  {sortField === 'isActive' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort('createdAt')}
                >
                  Created
                  {sortField === 'createdAt' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-12">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedUsers.has(user.id)}
                      onChange={() => handleSelectUser(user.id)}
                      className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {highlightText(user.firstName, effectiveSearchTerm)} {highlightText(user.lastName, effectiveSearchTerm)}
                        </div>
                        {user.isAttorney && (
                          <div className="text-xs text-lex-accent dark:text-lex-accent">Attorney</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {highlightText(user.email, effectiveSearchTerm)}
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {user.roles.map(role => {
                        // Color-code roles for better visibility
                        const getRoleVariant = (roleName: string) => {
                          const lowerRole = roleName.toLowerCase();
                          if (lowerRole === 'admin') return 'destructive';
                          if (lowerRole === 'attorney') return 'secondary';
                          if (lowerRole === 'billing') return 'success';
                          return 'outline';
                        };
                        
                        return (
                          <Badge key={role} variant={getRoleVariant(role)} className="text-xs">
                            {highlightText(role, effectiveSearchTerm)}
                          </Badge>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isSignatureReady(user) ? (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300">
                        Ready
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                        Setup Needed
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(user.isActive)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {canManageUsers && (
                      <div className="flex items-center justify-end gap-2">
                        {/* Primary Action: Manage Groups */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setUserForGroupManagement(user)}
                          className="flex items-center gap-1 text-lex-accent hover:text-lex-accent-hover border-lex-accent/30 hover:border-lex-accent/50 bg-lex-accent-soft hover:bg-lex-accent-soft/80"
                        >
                          <UsersIcon className="h-3 w-3" />
                          <span className="text-xs font-medium">Groups</span>
                        </Button>
                        
                        {/* Secondary Actions Dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger>
                            <Button variant="ghost" size="sm" className="h-10 w-10 p-0 hover:bg-gray-100 dark:hover:bg-gray-700">
                              <EllipsisVerticalIcon className="h-5 w-5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditingUser(user)}>
                              <PencilIcon className="h-4 w-4 mr-2" />
                              Edit User
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setUserToResetPassword(user)}>
                              <KeyIcon className="h-4 w-4 mr-2" />
                              Reset Password
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => resendInvitationMutation.mutate(user.id)}
                              disabled={resendInvitationMutation.isPending}
                            >
                              <EnvelopeIcon className="h-4 w-4 mr-2" />
                              Resend Invitation
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {user.isActive ? (
                              <DropdownMenuItem 
                                onClick={() => setUserToDeactivate(user)}
                                className="text-yellow-600 dark:text-yellow-400"
                              >
                                <XMarkIcon className="h-4 w-4 mr-2" />
                                Deactivate User
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem 
                                onClick={() => setUserToReactivate(user)}
                                className="text-green-600 dark:text-green-400"
                              >
                                <UsersIcon className="h-4 w-4 mr-2" />
                                Reactivate User
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={() => setUserToDelete(user)}
                              className="text-red-600 dark:text-red-400"
                            >
                              <TrashIcon className="h-4 w-4 mr-2" />
                              Delete User
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty state */}
        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 dark:text-gray-400 mb-4">
              {effectiveSearchTerm || userFilters.role || userFilters.isActive
                ? 'No users match your search criteria.'
                : 'No users found.'}
            </div>
            {canManageUsers && !effectiveSearchTerm && !userFilters.role && !userFilters.isActive && (
              <Button onClick={() => setShowCreateModal(true)}>
                <PlusIcon className="h-4 w-4 mr-2" />
                Add User
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {data?.pagination && data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {((data.pagination.page - 1) * data.pagination.limit) + 1} to{' '}
            {Math.min(data.pagination.page * data.pagination.limit, data.pagination.total)} of{' '}
            {data.pagination.total} users
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={data.pagination.page === 1}
              onClick={() => setCurrentPage(data.pagination.page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={data.pagination.page === data.pagination.totalPages}
              onClick={() => setCurrentPage(data.pagination.page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateUserModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            refetch();
          }}
        />
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          isOpen={!!editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={() => {
            setEditingUser(null);
            refetch();
          }}
        />
      )}

      {userForGroupManagement && (
        <UserGroupsModal
          user={userForGroupManagement}
          isOpen={!!userForGroupManagement}
          onClose={() => setUserForGroupManagement(null)}
          onSuccess={() => {
            setUserForGroupManagement(null);
            refetch();
          }}
        />
      )}

      {/* Confirmation Dialogs */}
      {userToDeactivate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Deactivate User
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to deactivate <strong>{userToDeactivate.firstName} {userToDeactivate.lastName}</strong>? 
              This will disable their account and prevent login, but their data will be preserved.
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Reason for Deactivation
              </label>
              <select
                value={deactivationReason}
                onChange={(e) => setDeactivationReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 dark:bg-gray-700 dark:text-white"
                disabled={deactivateUserMutation.isPending}
              >
                <option value="termination">Termination</option>
                <option value="role_change">Role Change</option>
                <option value="leave_of_absence">Leave of Absence</option>
                <option value="security_concern">Security Concern</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setUserToDeactivate(null);
                  setDeactivationReason('termination');
                }}
                disabled={deactivateUserMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                className="bg-yellow-600 hover:bg-yellow-700 text-white"
                onClick={() => deactivateUserMutation.mutate({ 
                  userId: userToDeactivate.id, 
                  reason: deactivationReason 
                })}
                disabled={deactivateUserMutation.isPending}
              >
                {deactivateUserMutation.isPending ? 'Deactivating...' : 'Deactivate User'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {userToReactivate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Reactivate User
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to reactivate <strong>{userToReactivate.firstName} {userToReactivate.lastName}</strong>? 
              This will enable their account and restore login access.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setUserToReactivate(null)}
                disabled={reactivateUserMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => reactivateUserMutation.mutate(userToReactivate.id)}
                disabled={reactivateUserMutation.isPending}
              >
                {reactivateUserMutation.isPending ? 'Reactivating...' : 'Reactivate User'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {userToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Permanently Delete User
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to <strong>permanently delete</strong> <strong>{userToDelete.firstName} {userToDelete.lastName}</strong>? 
              This action cannot be undone and will remove the user from both EdgeLex and Keycloak.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setUserToDelete(null)}
                disabled={deleteUserMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => deleteUserMutation.mutate(userToDelete.id)}
                disabled={deleteUserMutation.isPending}
              >
                {deleteUserMutation.isPending ? 'Deleting...' : 'Permanently Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {userToResetPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Reset Password
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to reset the password for <strong>{userToResetPassword.firstName} {userToResetPassword.lastName}</strong>? 
              They will receive instructions to set a new password.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setUserToResetPassword(null)}
                disabled={resetPasswordMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={() => resetPasswordMutation.mutate(userToResetPassword.id)}
                disabled={resetPasswordMutation.isPending}
              >
                {resetPasswordMutation.isPending ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`px-4 py-3 rounded-lg shadow-lg border-l-4 transition-all duration-300 max-w-sm ${
              toast.type === 'success'
                ? 'bg-green-50 border-green-400 text-green-800'
                : toast.type === 'warning'
                ? 'bg-yellow-50 border-yellow-400 text-yellow-800'
                : 'bg-red-50 border-red-400 text-red-800'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{toast.message}</span>
              <button
                onClick={() => dismissToast(toast.id)}
                className="ml-4 text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}; 
